// Remote Config: feature flags and the maintenance switch, with the phone
// app's keys and defaults. A slow network never holds the app up: after
// three seconds the cached or built-in values are used.
import { fetchAndActivate, getBoolean, getRemoteConfig, getString, type RemoteConfig } from 'firebase/remote-config';
import { firebaseApp, isProduction } from '@/data/firebase';

/**
 * Features switched on or off without a release (the phone app's
 * FeatureFlag). New features default to false; ones already out default to
 * true, so the flag works as a kill switch.
 */
export const FLAGS = {
  /** Public invoice links: the Link and Email share buttons. */
  invoiceLinks: { key: 'ff_invoice_links', defaultValue: true },
  /**
   * Pro is on sale. Until then every Pro feature is free for everyone and
   * Pro is never mentioned. Never makes anyone Pro.
   */
  proLaunched: { key: 'ff_pro_launched', defaultValue: false },
  /** The AI assistant; not on the web yet. */
  aiAssistant: { key: 'ff_ai_assistant', defaultValue: false },
} as const;

export type FlagName = keyof typeof FLAGS;
export type Flags = Record<FlagName, boolean>;

export const DEFAULT_FLAGS: Flags = Object.fromEntries(Object.entries(FLAGS).map(([name, f]) => [name, f.defaultValue])) as Flags;

export interface AppConfig {
  flags: Flags;
  maintenanceMode: boolean;
  maintenanceMessage: string;
}

const DEFAULTS: Record<string, string | boolean> = {
  web_min_version: '0.0.0',
  maintenance_mode: false,
  maintenance_message: '',
  ...Object.fromEntries(Object.values(FLAGS).map((f) => [f.key, f.defaultValue])),
};

let rc: RemoteConfig | null = null;

export async function loadConfig(): Promise<AppConfig> {
  try {
    rc ??= getRemoteConfig(firebaseApp());
    rc.defaultConfig = DEFAULTS;
    rc.settings.fetchTimeoutMillis = 3000;
    // Most visits read the browser's cached values instead of the network.
    rc.settings.minimumFetchIntervalMillis = isProduction ? 3_600_000 : 60_000;
    await fetchAndActivate(rc);
  } catch (error) {
    console.warn('Using the cached or built-in config', error);
  }
  return readConfig();
}

function readConfig(): AppConfig {
  if (!rc) return { flags: DEFAULT_FLAGS, maintenanceMode: false, maintenanceMessage: '' };
  const config = rc;
  return {
    flags: Object.fromEntries(Object.entries(FLAGS).map(([name, f]) => [name, getBoolean(config, f.key)])) as Flags,
    maintenanceMode: getBoolean(config, 'maintenance_mode'),
    maintenanceMessage: getString(config, 'maintenance_message').trim(),
  };
}
